import { api } from './api';
import type {
  Bookmark,
  CreateBookmark,
  UpdateBookmark,
  PaginatedResponse,
} from '@stashmark/shared';

export const bookmarksApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getBookmarks: builder.query<PaginatedResponse<Bookmark>, { page?: number; limit?: number }>({
      query: ({ page = 1, limit = 20 }) => ({
        url: '/bookmarks',
        params: { page, limit },
      }),
      transformResponse: (response: { success: boolean; data: PaginatedResponse<Bookmark> }) =>
        response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Bookmark' as const, id })),
              { type: 'Bookmark', id: 'LIST' },
            ]
          : [{ type: 'Bookmark', id: 'LIST' }],
    }),

    getBookmark: builder.query<Bookmark, string>({
      query: (id) => `/bookmarks/${id}`,
      transformResponse: (response: { success: boolean; data: Bookmark }) => response.data,
      providesTags: (_, __, id) => [{ type: 'Bookmark', id }],
    }),

    createBookmark: builder.mutation<Bookmark, CreateBookmark>({
      query: (bookmark) => ({
        url: '/bookmarks',
        method: 'POST',
        body: bookmark,
      }),
      transformResponse: (response: { success: boolean; data: Bookmark }) => response.data,
      invalidatesTags: [{ type: 'Bookmark', id: 'LIST' }],
    }),

    updateBookmark: builder.mutation<Bookmark, { id: string; data: UpdateBookmark }>({
      query: ({ id, data }) => ({
        url: `/bookmarks/${id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: { success: boolean; data: Bookmark }) => response.data,
      invalidatesTags: (_, __, { id }) => [
        { type: 'Bookmark', id },
        { type: 'Bookmark', id: 'LIST' },
      ],
    }),

    deleteBookmark: builder.mutation<void, string>({
      query: (id) => ({
        url: `/bookmarks/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [
        { type: 'Bookmark', id },
        { type: 'Bookmark', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetBookmarksQuery,
  useGetBookmarkQuery,
  useCreateBookmarkMutation,
  useUpdateBookmarkMutation,
  useDeleteBookmarkMutation,
} = bookmarksApi;
